import React, { useContext } from "react";
import { useTranslation } from "react-i18next";
import LayoutMasterDetail from "layouts/LayoutMasterDetail";
import { StoreContext } from "shared/context/store-context";
import { personelServiceRouteTypes_tr, personelServiceRouteTypes_en } from "shared/util/types";
import { getCurrentLanguage } from "shared/utility/lang";
import VehicleRouteBulkProcess from "components/shared/automations/CreateSchoolRoutesAutomation";
import { noRealSchoolBlocker } from "shared/utility/blocker-conditions";
import VehicleRouteItem from "features/services/student-services/VehicleRouteItem";
import ServiceMetricCell from "features/services/shared/ServiceMetricCell";
import { getServiceRouteTimeColumns } from "features/services/shared/serviceRouteTimeColumns";
import { useOpenFirstParam } from "shared/hooks/useOpenFirstParam";
import RideSkipCountCell from "features/ride-skip/RideSkipCountCell";
import { useRideSkipSummary } from "features/ride-skip/useRideSkipSummary";
import { RIDE_SKIP_SERVICE_KINDS } from "features/ride-skip/constants";

const VehicleRoutesList = () => {
  const { t } = useTranslation();
  const openFirst = useOpenFirstParam();
  const { enabled: skipEnabled, counts: skipCounts } = useRideSkipSummary();

  const itemType = "vehicleRoute";
  const listName = "main_vehicleRoutes";

  const currentLanguage = getCurrentLanguage();
  const vehicleroutetypes = currentLanguage === "tr" ? personelServiceRouteTypes_tr : personelServiceRouteTypes_en;

  const { vehicleRoutes, studentSchoolOptions, schools, DeleteVehicleRoute, InsertVehicleRoute, UpdateVehicleRoute } =
    useContext(StoreContext);

  let listColumns = [];

  if (studentSchoolOptions?.length > 1) {
    listColumns.push({
      field: "school",
      headerName: t("client"),
      width: 350,
      valueGetter: (params) => studentSchoolOptions.find((school) => school.value === params.row?.school?.id)?.label,
    });
  }

  listColumns.push({ field: "name", headerName: t("name"), width: 350 });

  listColumns.push({
    field: "routeType",
    headerName: t("serviceType"),
    width: 200,
    valueGetter: (params) => vehicleroutetypes.find((item) => item.value === params.row?.routeType)?.label || "-",
  });

  listColumns.push({
    field: "driver",
    headerName: t("driver"),
    width: 300,
    valueGetter: (params) =>
      Array.isArray(params.row?.driver) && params.row.driver.length > 0
        ? params.row.driver.map((driver) => driver.name).join(", ")
        : "-",
  });

  listColumns.push({
    field: "numOfStudents",
    headerName: t("numberofpassengers"),
    width: 200,
    valueGetter: (params) => params.row?.student?.length?.toString() || "-",
  });

  if (skipEnabled) {
    listColumns.push({
      field: "rideSkipCount",
      headerName: t("rideSkip.listColumn"),
      width: 170,
      valueGetter: (params) => skipCounts[`${RIDE_SKIP_SERVICE_KINDS.VEHICLE_ROUTE}:${params.row.id}`] || 0,
      renderCell: (params) => (
        <RideSkipCountCell count={skipCounts[`${RIDE_SKIP_SERVICE_KINDS.VEHICLE_ROUTE}:${params.row.id}`]} />
      ),
    });
  }

  listColumns.push(...getServiceRouteTimeColumns(t));

  // Rota metriği (mesafe/süre). Eksikse listede kırmızı "Hesaplanmadı" rozeti gösterilir.
  listColumns.push({
    field: "routeMetrics",
    headerName: t("routeMetrics"),
    width: 200,
    valueGetter: (params) => {
      const duration = Number(params.row?.totalDuration);
      return Number.isFinite(duration) ? duration : 0;
    },
    renderCell: (params) => <ServiceMetricCell row={params.row} />,
  });

  return (
    <LayoutMasterDetail
      itemType={itemType}
      items={vehicleRoutes}
      listName={listName}
      listColumns={listColumns}
      SelectedItemComponent={VehicleRouteItem}
      NewItemComponent={VehicleRouteItem}
      DeleteItem={DeleteVehicleRoute}
      InsertItem={InsertVehicleRoute}
      UpdateItem={UpdateVehicleRoute}
      isAlwaysFullScreen={true}
      hideCreateNewItemWhenSelected={true}
      BulkProcessComponent={VehicleRouteBulkProcess}
      blockerConditions={[noRealSchoolBlocker(schools)]}
      autoOpenFirstItem={openFirst}
    />
  );
};

export default VehicleRoutesList;
